import React from "react";
import { Modal, Table, Checkbox, Button } from "antd";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";

interface ItemsApproveBySchemeModalProps {
  isModalOpen: boolean;
  handleCancel: () => void;
  handleSave: () => void;
  tableData: any[];
  setTableData: (data: any[]) => void;
}

const ItemsApproveBySchemeModal: React.FC<ItemsApproveBySchemeModalProps> = ({
  isModalOpen,
  handleCancel,
  handleSave,
  tableData,
  setTableData,
}) => {
  const { patients } = useSelector((state: RootState) => state);
  const isTPAScheme =
    patients?.fetchBasicDetails?.casesResponse?.schemeType?.toUpperCase() ===
    "TPA";

  const tpaColumns = [
    {
      title: "Sr No",
      dataIndex: "srNo",
      key: "srNo",
      width: 90,
      render: (_: any, record: any, index: number) => index + 1,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 110,
    },
    {
      title: "Particulars",
      dataIndex: "particular",
      key: "particular",
      width: 150,
    },
    {
      title: "Category",
      dataIndex: "collection",
      key: "collection",
      width: 175,
      render: (_: any, record: any) => record?.collection?.name || "-",
    },
    {
      title: "Sub Category",
      dataIndex: "subCollection",
      key: "subCollection",
      width: 175,
      render: (_: any, record: any) => record?.subCollection?.name || "-",
    },
    {
      title: "Unit/Days",
      dataIndex: "unitPerDay",
      key: "unitPerDay",
      width: 100,
      render: (text: any) => (text ? text : "-"),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 100,
      render: (text: any) => (text ? `₹${text}` : "-"),
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      width: 100,
      render: (text: any) => (text ? `₹${text}` : "-"),
    },
    {
      title: "Soc Rate",
      dataIndex: "socRate",
      key: "socRate",
      width: 100,
      render: (text: any) => (text ? `₹${text}` : "-"),
    },
    {
      title: "Tariff Rate",
      dataIndex: "tariffRate",
      key: "tariffRate",
      width: 100,
      render: (text: any) => (text ? `₹${text}` : "-"),
    },
    {
      title: "Approved by Scheme",
      dataIndex: "isItemApprovedOnScheme",
      key: "isItemApprovedOnScheme",
      width: 100,
      render: (_: any, record: any) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Checkbox
            checked={record.isItemApprovedOnScheme}
            onChange={(e) => handleCheckboxChange(record.id, e.target.checked)}
          />
        </div>
      ),
    },
  ];

  const govtColumns = [
    {
      title: "Sr No",
      dataIndex: "srNo",
      key: "srNo",
      width: 90,
      render: (_: any, record: any, index: number) => index + 1,
    },
    {
      title: "Service Date",
      dataIndex: "serviceDate",
      key: "serviceDate",
      width: 110,
    },
    {
      title: "Service Name",
      dataIndex: "serviceName",
      key: "serviceName",
      width: 150,
    },
    {
      title: "Category",
      dataIndex: "collections",
      key: "collections",
      width: 190,
      render: (_: any, record: any) => record?.collections?.name || "-",
    },
    {
      title: "Sub Category",
      dataIndex: "subCollections",
      key: "subCollections",
      width: 190,
      render: (_: any, record: any) => record?.subCollections?.name || "-",
    },
    {
      title: "Qty",
      dataIndex: "serviceQty",
      key: "serviceQty",
      width: 100,
      render: (text: any) => (text ? text : "-"),
    },
    {
      title: "Rate",
      dataIndex: "serviceRate",
      key: "serviceRate",
      width: 100,
      render: (text: any) => (text ? `₹${text}` : "-"),
    },
    {
      title: "Amount",
      dataIndex: "serviceAmount",
      key: "serviceAmount",
      width: 100,
      render: (text: any) => (text ? `₹${text}` : "-"),
    },
    {
      title: "Scheme Code",
      dataIndex: "schemeCodeWithName",
      key: "schemeCodeWithName",
      width: 150,
    },
    {
      title: "Scheme Rate",
      dataIndex: "serviceSchemeRate",
      key: "serviceSchemeRate",
      width: 100,
      render: (text: any) => (text ? `₹${text}` : "-"),
    },
    {
      title: "Total Scheme Rate",
      dataIndex: "serviceSchemeTotalRate",
      key: "serviceSchemeTotalRate",
      width: 100,
      render: (text: any) => (text ? `₹${text}` : "-"),
    },
    {
      title: "Variation Rate",
      dataIndex: "variationRate",
      key: "variationRate",
      width: 100,
      render: (text: any) => (text ? `₹${text}` : "-"),
    },
    {
      title: "Approved by Scheme",
      dataIndex: "isItemApprovedOnScheme",
      key: "isItemApprovedOnScheme",
      width: 100,
      render: (_: any, record: any) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Checkbox
            checked={record.isItemApprovedOnScheme}
            onChange={(e) => handleCheckboxChange(record.id, e.target.checked)}
          />
        </div>
      ),
    },
  ];

  const handleCheckboxChange = (id: number, checked: boolean) => {
    setTableData(
      tableData.map((item) =>
        item.id === id ? { ...item, isItemApprovedOnScheme: checked } : item
      )
    );
  };

  const tableCss = `
    /* header background */
    .items-approve-table .ant-table-thead > tr > th {
      background: #004c8f !important; /* blue */
      color: #ffffff !important;
      vertical-align: middle;
    }
    /* make table container full width */
    .items-approve-modal .ant-modal-content {
      max-width: 95vw;
    }
    .items-approve-table .ant-table {
      width: 100% !important;
    }
    /* keep horizontal scroll when columns overflow */
    .items-approve-table .ant-table-container {
      overflow: auto;
    }
  `;

  return (
    <>
      <style>{tableCss}</style>
      <Modal
        title="Items Approved By Scheme"
        open={isModalOpen}
        onCancel={handleCancel}
        width={"100%"}
        height={"100%"}
        className="package-modal"
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleSave}>
            Save
          </Button>,
        ]}
      >
        <Table
          columns={isTPAScheme ? tpaColumns : govtColumns}
          dataSource={tableData}
          scroll={{ x: 1000, y: 400 }}
          pagination={false}
          rowKey="id"
          className="items-approve-table"
          style={{ width: "100%" }}
        />
      </Modal>
    </>
  );
};

export default ItemsApproveBySchemeModal;
